/**
 * Active in-app toast queue for incoming push notifications.
 *
 * Lifecycle:
 *   1. `enqueue()` adds a toast and schedules its auto-dismiss timer.
 *   2. The host (`PushToastHost`) subscribes via React and renders the stack.
 *   3. `dismiss()` removes it early (user clicked X / clicked the body).
 *
 * Capacity: only the latest `MAX_VISIBLE_TOASTS` are kept on screen so a
 * burst of pushes can't bury the whole viewport.
 */

import { create } from 'zustand';
import type { InAppNotification } from './parseInAppPush';

const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION_MS = 6000;
/** Suppress repeats of the same `key` within this window. */
const DEDUPE_WINDOW_MS = 2500;

interface ToastEntry extends InAppNotification {
  /** Wall-clock time when the toast appeared. */
  shownAt: number;
}

interface PushToastState {
  toasts: readonly ToastEntry[];
  enqueue: (notification: InAppNotification) => void;
  dismiss: (key: string) => void;
  clear: () => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

function clearTimer(key: string) {
  const t = timers.get(key);
  if (t) {
    clearTimeout(t);
    timers.delete(key);
  }
}

export const usePushToastStore = create<PushToastState>((set, get) => ({
  toasts: [],
  enqueue: (notification) => {
    const now = Date.now();
    const existing = get().toasts.find((t) => t.key === notification.key);
    // De-dupe rapid repeats — the same push can land via both onMessage and
    // the SW raw-push fallback for data-only payloads.
    if (existing && now - existing.shownAt < DEDUPE_WINDOW_MS) {
      return;
    }

    set((state) => {
      const next = [
        { ...notification, shownAt: now },
        ...state.toasts.filter((t) => t.key !== notification.key),
      ].slice(0, MAX_VISIBLE_TOASTS);
      return { toasts: next };
    });

    clearTimer(notification.key);
    timers.set(
      notification.key,
      setTimeout(() => {
        timers.delete(notification.key);
        // Use a fresh getter — the user may have already dismissed.
        usePushToastStore.getState().dismiss(notification.key);
      }, DEFAULT_DURATION_MS),
    );
  },
  dismiss: (key) => {
    clearTimer(key);
    set((state) => ({ toasts: state.toasts.filter((t) => t.key !== key) }));
  },
  clear: () => {
    for (const key of Array.from(timers.keys())) clearTimer(key);
    set({ toasts: [] });
  },
}));
